import { useRef, useState } from 'react';
import { TbCloudUpload } from 'react-icons/tb';
import { MAX_UPLOAD_BYTES } from '../import-api';

const ACCEPT = '.xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function extensionOf(name) {
  const value = String(name || '');
  return value.slice(value.lastIndexOf('.') + 1).toLowerCase();
}

// Client-side checks only cover what the plan explicitly asks for (extension
// + size) — everything else about the file's content (headers, row count,
// whether it's actually a readable spreadsheet) is the server's job
// (`createUpload` in member-import-service.js), so this never tries to
// second-guess it.
function validate(file) {
  if (!file) return 'Elegí un archivo.';
  if (!['xlsx', 'csv'].includes(extensionOf(file.name))) return 'Subí un archivo .xlsx o .csv.';
  if (file.size === 0) return 'El archivo está vacío.';
  if (file.size > MAX_UPLOAD_BYTES) return 'El archivo supera el máximo de 5 MB.';
  return '';
}

/**
 * Step 1 of the import wizard (plan task E6): drag-and-drop or pick a
 * `.xlsx`/`.csv` file. `onUpload(file)` is only called once this component's
 * own extension/size checks pass — the same limits the server enforces
 * (`MAX_UPLOAD_BYTES`, `.xlsx`/`.csv` only) — so an obviously-wrong file never
 * costs the admin a round trip just to hear the same rejection back.
 */
export function UploadStep({ onUpload, submitting, error }) {
  const [localError, setLocalError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  function handleFile(file) {
    const message = validate(file);
    if (message) {
      setLocalError(message);
      return;
    }
    setLocalError('');
    onUpload(file);
  }

  function handleInputChange(event) {
    const file = event.target.files && event.target.files[0];
    // Reset so choosing the exact same file again (after fixing it outside
    // the browser) still fires a change event.
    event.target.value = '';
    handleFile(file);
  }

  function handleDrop(event) {
    event.preventDefault();
    setDragOver(false);
    if (submitting) return;
    const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
    handleFile(file);
  }

  const shownError = localError || error;

  return (
    <div className="gym-card gym-import-upload">
      <p className="gym-muted-text">
        Subí el archivo del sistema actual (.xlsx o .csv, hasta 5 MB). En el paso siguiente vas a poder
        elegir qué columna corresponde a cada dato.
      </p>

      <div
        data-testid="gym-import-dropzone"
        className={dragOver ? 'gym-import-dropzone gym-import-dropzone--active' : 'gym-import-dropzone'}
        onDragOver={(event) => { event.preventDefault(); if (!submitting) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <TbCloudUpload className="gym-import-dropzone__icon" aria-hidden="true" />
        <p className="gym-import-dropzone__lead">Arrastrá el archivo acá</p>
        <p className="gym-muted-text">o</p>
        <button
          type="button"
          className="gym-button gym-button--primary"
          disabled={submitting}
          onClick={() => inputRef.current && inputRef.current.click()}
        >
          Elegir archivo
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          aria-label="Archivo de socios"
          className="gym-sr-only"
          disabled={submitting}
          onChange={handleInputChange}
        />
        <p className="gym-import-dropzone__hint gym-muted-text">Formatos aceptados: .xlsx o .csv, hasta 5 MB.</p>
      </div>

      {submitting && (
        <p role="status" aria-live="polite" className="gym-import-status">
          <span className="gym-state__spinner" aria-hidden="true" />
          Leyendo archivo…
        </p>
      )}

      {shownError && <p role="alert" className="gym-form__error">{shownError}</p>}
    </div>
  );
}
